import { ApiHttpService } from 'src/app/_services/api-http.service';
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { StorageService } from 'src/app/_services/storage.service';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, of } from 'rxjs';
import { IAllTransactionResponseDTO, IApiEnvelope, IPageResponse, ITransactionFilter } from '../models/transaction-report.models';

const { API_URLBLAZE } = environment;

@Injectable({
  providedIn: 'root'
})
export default class TransactionService {

  constructor(
    private apiHttpService: ApiHttpService,
    private http: HttpClient,
    private storageService: StorageService,
    private router: Router
  ) { }


  getTransactionList(filterData: ITransactionFilter): Observable<IApiEnvelope<IPageResponse<IAllTransactionResponseDTO>>> {
    const params = this.buildQueryParams(filterData);
    const url = `${API_URLBLAZE}/transaction/v2/transaction-report`;
    return this.apiHttpService.get(url, { params }).pipe(
      catchError(this.handleError<IApiEnvelope<IPageResponse<IAllTransactionResponseDTO>>>())
    ) as Observable<IApiEnvelope<IPageResponse<IAllTransactionResponseDTO>>>;
  }

  exportTransactionCSV(filterData: ITransactionFilter): Observable<Blob> {
    const params = this.buildExportQueryParams(filterData);
    const url = `${API_URLBLAZE}/transaction/v2/transaction-report/export`;
    return this.http.get(url, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.storageService.clean();
          this.router.navigate(['/auth/login']);
        }
        throw error;
      })
    );
  }


  private buildQueryParams(filter: ITransactionFilter): HttpParams {
    let params = new HttpParams();

    if (filter.page_no !== undefined) {
      params = params.set('page_no', filter.page_no.toString());
    }
    if (filter.page_size !== undefined) {
      params = params.set('page_size', filter.page_size.toString());
      params = params.set('pageNumber', filter.page_size.toString());
    }

    if (filter.sort_column) {
      const dir = filter.sort_dir ? filter.sort_dir.toUpperCase() : 'DESC';
      params = params.set('sort', `${filter.sort_column},${dir}`);
    }

    // Process new style filter expressions
    if (filter.filters && Array.isArray(filter.filters)) {
      filter.filters.forEach(f => {
        if (f && f.trim() !== '') {
          params = params.append('filter', f.trim());
        }
      });
    }

    // Legacy/Individual filters converted to the new format for complete backward compatibility
    if (filter.startDate && filter.startDate.trim() !== '') {
      params = params.append('filter', `createDate:gte:${filter.startDate.trim()}`);
    }
    if (filter.endDate && filter.endDate.trim() !== '') {
      params = params.append('filter', `createDate:lte:${filter.endDate.trim()}`);
    }

    if (filter.merchantName && filter.merchantName.trim() !== '') {
      params = params.append('filter', `merchantName:contains:${filter.merchantName.trim()}`);
    }
    if (filter.merchantId && filter.merchantId.trim() !== '') {
      params = params.append('filter', `merchantId:eq:${filter.merchantId.trim()}`);
    }

    if (filter.customerId && filter.customerId.trim() !== '') {
      params = params.append('filter', `customerId:eq:${filter.customerId.trim()}`);
    }
    if (filter.customerName && filter.customerName.trim() !== '') {
      params = params.append('filter', `customerName:contains:${filter.customerName.trim()}`);
    }
    if (filter.customerMobile && filter.customerMobile.trim() !== '') {
      params = params.append('filter', `customerMobile:eq:${filter.customerMobile.trim()}`);
    }

    if (filter.status && filter.status.trim() !== '') {
      params = params.append('filter', `status:eq:${filter.status.trim()}`);
    }
    if (filter.transactionType && filter.transactionType.trim() !== '') {
      params = params.append('filter', `transactionType:eq:${filter.transactionType.trim()}`);
    }
    if (filter.orderId && filter.orderId.trim() !== '') {
      params = params.append('filter', `orderId:eq:${filter.orderId.trim()}`);
    }

    if (filter.amount && filter.amount.toString().trim() !== '') {
      params = params.append('filter', `amount:eq:${filter.amount.toString().trim()}`);
    }
    if (filter.currency && filter.currency.trim() !== '') {
      params = params.append('filter', `currency:eq:${filter.currency.trim()}`);
    }

    return params;
  }

  private buildExportQueryParams(filter: ITransactionFilter): HttpParams {
    let params = new HttpParams();

    // Process new style filter expressions
    if (filter.filters && Array.isArray(filter.filters)) {
      filter.filters.forEach(f => {
        if (f && f.trim() !== '') {
          params = params.append('filter', f.trim());
        }
      });
    }

    // Legacy/Individual filters converted to the new format for complete backward compatibility
    if (filter.startDate && filter.startDate.trim() !== '') {
      params = params.append('filter', `createDate:gte:${filter.startDate.trim()}`);
    }
    if (filter.endDate && filter.endDate.trim() !== '') {
      params = params.append('filter', `createDate:lte:${filter.endDate.trim()}`);
    }

    if (filter.merchantName && filter.merchantName.trim() !== '') {
      params = params.append('filter', `merchantName:contains:${filter.merchantName.trim()}`);
    }
    if (filter.merchantId && filter.merchantId.trim() !== '') {
      params = params.append('filter', `merchantId:eq:${filter.merchantId.trim()}`);
    }

    if (filter.customerId && filter.customerId.trim() !== '') {
      params = params.append('filter', `customerId:eq:${filter.customerId.trim()}`);
    }
    if (filter.customerName && filter.customerName.trim() !== '') {
      params = params.append('filter', `customerName:contains:${filter.customerName.trim()}`);
    }
    if (filter.customerMobile && filter.customerMobile.trim() !== '') {
      params = params.append('filter', `customerMobile:eq:${filter.customerMobile.trim()}`);
    }

    if (filter.status && filter.status.trim() !== '') {
      params = params.append('filter', `status:eq:${filter.status.trim()}`);
    }
    if (filter.transactionType && filter.transactionType.trim() !== '') {
      params = params.append('filter', `transactionType:eq:${filter.transactionType.trim()}`);
    }
    if (filter.orderId && filter.orderId.trim() !== '') {
      params = params.append('filter', `orderId:eq:${filter.orderId.trim()}`);
    }

    if (filter.amount && filter.amount.toString().trim() !== '') {
      params = params.append('filter', `amount:eq:${filter.amount.toString().trim()}`);
    }
    if (filter.currency && filter.currency.trim() !== '') {
      params = params.append('filter', `currency:eq:${filter.currency.trim()}`);
    }

    return params;
  }


  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      if (error.status === 401) {
        this.storageService.clean();
        this.router.navigate(['/auth/login']);
      }
      result = error?.error;
      return of(result as T);
    };
  }
}
